import React, { useState, useEffect } from 'react';
import { useAdmin } from '../context/AdminContext';
import { 
  X, Save, RotateCcw, Search, Image, Link, Type, 
  Layout, Home, Package, FileText, Phone, Menu,
  ChevronRight, Check, AlertCircle, Upload, Building2
} from 'lucide-react';

const sectionConfig = {
  header: { icon: Menu, label: 'Шапка сайта', color: 'blue' },
  navigation: { icon: Menu, label: 'Навигация', color: 'blue' },
  hero: { icon: Layout, label: 'Главный баннер', color: 'purple' },
  stats: { icon: FileText, label: 'Статистика', color: 'indigo' },
  products: { icon: Package, label: 'Продукция', color: 'green' },
  projects: { icon: Building2, label: 'Проекты', color: 'orange' },
  clients: { icon: Building2, label: 'Нам доверяют', color: 'yellow' },
  services: { icon: Layout, label: 'Применение', color: 'cyan' },
  specs: { icon: FileText, label: 'Характеристики', color: 'slate' },
  about: { icon: FileText, label: 'О компании', color: 'teal' },
  contact: { icon: Phone, label: 'Контакты', color: 'red' },
  contacts: { icon: Phone, label: 'Контакты (стр)', color: 'red' },
  catalog: { icon: Package, label: 'Каталог', color: 'emerald' },
  installation: { icon: Layout, label: 'Монтаж', color: 'amber' },
  video: { icon: FileText, label: 'Видео', color: 'pink' },
  certificates: { icon: FileText, label: 'Сертификаты', color: 'violet' },
  footer: { icon: Layout, label: 'Подвал сайта', color: 'gray' },
  whatsapp: { icon: Phone, label: 'WhatsApp', color: 'green' },
};

const typeConfig = {
  text: { icon: Type, label: 'Текст', color: 'blue' },
  link: { icon: Link, label: 'Ссылка', color: 'green' },
  image: { icon: Image, label: 'Изображение', color: 'purple' },
};

const ContentPanel = () => {
  const { 
    showContentPanel, 
    setShowContentPanel, 
    siteContent, 
    contentLoading,
    unsavedChanges,
    saveAllChanges,
    updateContentLocal,
    saveContent,
    resetToDefault,
    uploadImage,
    hasUnsavedChanges,
    sessionId
  } = useAdmin();

  const [activeSection, setActiveSection] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!showContentPanel) return null;

  // Get all content items as flat array
  const getAllItems = () => {
    const items = [];
    Object.entries(siteContent).forEach(([section, sectionItems]) => {
      Object.values(sectionItems).forEach(item => {
        items.push({ ...item, section });
      });
    });
    return items;
  };

  const allItems = getAllItems();
  
  // Filter items
  const filteredItems = allItems.filter(item => {
    const matchesSection = activeSection === 'all' || item.section === activeSection;
    const matchesSearch = !searchQuery || 
      item.label?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.key?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      JSON.stringify(item.value).toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSection && matchesSearch;
  });

  // Group by section
  const groupedItems = filteredItems.reduce((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});

  const handleSaveAll = async () => {
    setSaving(true);
    await saveAllChanges();
    setSaving(false);
  };

  const sections = ['all', ...Object.keys(sectionConfig)];

  return (
    <div className="fixed inset-0 z-[100] flex">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => setShowContentPanel(false)}
      />
      
      {/* Panel */}
      <div className="relative ml-auto w-full max-w-4xl bg-white shadow-2xl flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-[#0E2A47] to-[#1a3d5c] text-white">
          <div>
            <h2 className="text-xl font-bold">Управление контентом</h2>
            <p className="text-sm text-blue-200">Редактируйте тексты, ссылки и изображения сайта</p>
          </div>
          <div className="flex items-center gap-3">
            {hasUnsavedChanges && (
              <button
                onClick={handleSaveAll}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-medium"
              >
                {saving ? (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Сохранить всё
              </button>
            )}
            <button
              onClick={() => setShowContentPanel(false)}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Поиск по контенту..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
              />
            </div>
          </div>

          {/* Section tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {sections.map(section => {
              const config = sectionConfig[section] || { icon: Layout, label: 'Все', color: 'gray' };
              const Icon = section === 'all' ? Layout : config.icon;
              const isActive = activeSection === section;
              const count = section === 'all' ? allItems.length : (siteContent[section] ? Object.keys(siteContent[section]).length : 0);
              
              return (
                <button
                  key={section}
                  onClick={() => setActiveSection(section)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                    isActive 
                      ? 'bg-[#061d36] text-white shadow-lg' 
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {section === 'all' ? 'Все' : config.label}
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content List */}
        <div className="flex-1 overflow-y-auto p-6">
          {contentLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
            </div>
          ) : Object.keys(groupedItems).length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Контент не найден</p>
              <p className="text-sm mt-1">Попробуйте изменить фильтры или поисковый запрос</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedItems).map(([section, items]) => {
                const config = sectionConfig[section] || { icon: Layout, label: section, color: 'gray' };
                const Icon = config.icon;
                
                return (
                  <div key={section} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className={`px-4 py-3 bg-gradient-to-r from-${config.color}-50 to-white border-b flex items-center gap-2`}>
                      <Icon className={`w-5 h-5 text-${config.color}-500`} />
                      <span className="font-semibold text-[#061d36]">{config.label}</span>
                      <span className="text-sm text-gray-500">({items.length})</span>
                    </div>
                    
                    <div className="divide-y divide-gray-100">
                      {items.map(item => (
                        <ContentItemRow 
                          key={item.id} 
                          item={item}
                          isEditing={editingItem === item.id}
                          onEdit={() => setEditingItem(item.id)}
                          onClose={() => setEditingItem(null)}
                          onSave={saveContent}
                          onReset={resetToDefault}
                          onUpdate={updateContentLocal}
                          uploadImage={uploadImage}
                          hasChanges={!!unsavedChanges[item.id]}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {hasUnsavedChanges && (
          <div className="px-6 py-3 border-t bg-yellow-50 flex items-center justify-between">
            <div className="flex items-center gap-2 text-yellow-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">
                Есть несохранённые изменения ({Object.keys(unsavedChanges).length})
              </span>
            </div>
            <button
              onClick={handleSaveAll}
              disabled={saving}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors font-medium text-sm"
            >
              Сохранить всё
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Content Item Row Component
const ContentItemRow = ({ 
  item, 
  isEditing, 
  onEdit, 
  onClose, 
  onSave, 
  onReset, 
  onUpdate,
  uploadImage,
  hasChanges 
}) => {
  const [localValue, setLocalValue] = useState(item.value);
  const [saving, setSaving] = useState(false);
  const TypeIcon = typeConfig[item.type]?.icon || Type;

  useEffect(() => {
    setLocalValue(item.value);
  }, [item.value]);

  const handleSave = async () => {
    setSaving(true);
    onUpdate(item.id, localValue);
    await onSave(item.id, localValue);
    setSaving(false);
    onClose();
  };

  const handleReset = async () => {
    if (window.confirm('Сбросить к значению по умолчанию?')) {
      await onReset(item.id);
      onClose();
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    const url = await uploadImage(file);
    if (url) {
      setLocalValue({ ...localValue, url });
    }
    setSaving(false);
  };

  const getDisplayValue = () => {
    if (item.type === 'image') {
      return localValue?.url || 'Нет изображения';
    }
    const val = localValue;
    if (typeof val === 'object') {
      return val.ru || val.text || val.url || JSON.stringify(val);
    }
    return String(val);
  };

  return (
    <div className={`px-4 py-3 hover:bg-gray-50 transition-colors ${hasChanges ? 'bg-yellow-50' : ''}`}>
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg bg-${typeConfig[item.type]?.color || 'gray'}-100`}>
          <TypeIcon className={`w-4 h-4 text-${typeConfig[item.type]?.color || 'gray'}-500`} />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-[#061d36]">{item.label}</span>
            {hasChanges && (
              <span className="px-2 py-0.5 bg-yellow-200 text-yellow-700 text-xs rounded-full">
                изменено
              </span>
            )}
          </div>
          
          {isEditing ? (
            <div className="mt-2 space-y-3">
              {item.type === 'image' ? (
                <div className="space-y-2">
                  {localValue?.url && (
                    <img src={localValue.url} alt="" className="h-20 object-contain rounded border" />
                  )}
                  <div className="flex gap-2">
                    <label className="flex items-center gap-2 px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg cursor-pointer transition-colors text-sm">
                      <Upload className="w-4 h-4" />
                      Загрузить
                      <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                    </label>
                    <input
                      type="url"
                      value={localValue?.url || ''}
                      onChange={(e) => setLocalValue({ ...localValue, url: e.target.value })}
                      placeholder="или вставьте URL"
                      className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
                    />
                  </div>
                </div>
              ) : item.type === 'link' ? (
                <input
                  type="url"
                  value={localValue?.url || localValue || ''}
                  onChange={(e) => setLocalValue({ url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-green-500"
                />
              ) : (
                <div className="space-y-2">
                  {['ru', 'kz', 'en'].map(lang => (
                    <div key={lang} className="flex items-center gap-2">
                      <span className="w-8 text-xs font-medium text-gray-500 uppercase">{lang}</span>
                      <input
                        type="text"
                        value={localValue?.[lang] || ''}
                        onChange={(e) => setLocalValue({ ...localValue, [lang]: e.target.value })}
                        placeholder={`Текст на ${lang === 'ru' ? 'русском' : lang === 'kz' ? 'казахском' : 'английском'}`}
                        className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                      />
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={handleReset}
                  className="flex items-center gap-1 px-3 py-1.5 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors text-sm"
                >
                  <RotateCcw className="w-4 h-4" />
                  Сбросить
                </button>
                <button
                  onClick={onClose}
                  className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors text-sm"
                >
                  Отмена
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1 px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  {saving ? (
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <Check className="w-4 h-4" />
                  )}
                  Сохранить
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500 truncate max-w-md">
                {getDisplayValue()}
              </p>
              <button
                onClick={onEdit}
                className="flex items-center gap-1 px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors text-sm"
              >
                Изменить
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentPanel;
